import { useState, type ChangeEvent } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  Plus,
  Search,
} from "lucide-react";
import { StatCard } from "@/components/dashboard/StatCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Placeholder components for missing donation components
const DonationCard = ({
  id,
  title,
  category,
  subcategory,
  required,
  collected,
  description,
  onEdit,
  onDelete,
}: any) => {
  const progress = required > 0 ? (collected / required) * 100 : 0;
  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">
          {category} - {subcategory}
        </p>
        <p className="mt-2 text-sm">{description}</p>
        <div className="mt-4">
          <div className="mb-2 flex justify-between text-sm">
            <span>
              ${collected.toLocaleString()} / ${required.toLocaleString()}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-blue-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => onEdit(id)}>
            Edit
          </Button>
          <Button
            variant="outline"
            onClick={() => onDelete(id)}
            className="text-red-600 hover:text-red-700"
          >
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const DonorTable = ({ donors }: { donors: any[] }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="space-y-4">
        {donors.map(donor => (
          <div key={donor.id} className="flex justify-between border-b pb-2">
            <div>
              <p className="font-semibold">{donor.name}</p>
              <p className="text-sm text-muted-foreground">
                {donor.invoiceRef}
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold">${donor.amount}</p>
              <p className="text-sm text-muted-foreground">{donor.date}</p>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const CreateDonationModal = (_props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => null;
const CreateDonationRecordModal = (_props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => null;
const EditDonationModal = (_props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donationId: number | null;
}) => null;
const DeleteDonationModal = (_props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donationId: number | null;
}) => null;

const mockDonations = [
  {
    id: 1,
    title: "Pinjrapole",
    category: "Animal Welfare",
    subcategory: "Item Donation",
    required: 20000,
    collected: 10000,
    description:
      "Support our animal welfare initiative with essential supplies and care items.",
  },
  {
    id: 2,
    title: "Community Food Bank",
    category: "Social Welfare",
    subcategory: "Monetary Donation",
    required: 50000,
    collected: 35000,
    description:
      "Help us provide nutritious meals to families in need across the city.",
  },
  {
    id: 3,
    title: "Education Fund",
    category: "Education",
    subcategory: "Scholarship",
    required: 100000,
    collected: 75000,
    description:
      "Empower underprivileged students with access to quality education.",
  },
  {
    id: 4,
    title: "Healthcare Drive",
    category: "Healthcare",
    subcategory: "Medical Supplies",
    required: 30000,
    collected: 18000,
    description:
      "Provide essential medical supplies to rural healthcare centers.",
  },
];

const mockDonors = [
  {
    id: 1,
    name: "John Doe",
    amount: 5000,
    invoiceRef: "INV-2024-001",
    date: "2024-01-15",
    type: "Individual",
  },
  {
    id: 2,
    name: "Tech Corp Inc.",
    amount: 25000,
    invoiceRef: "INV-2024-002",
    date: "2024-01-18",
    type: "Corporate",
  },
  {
    id: 3,
    name: "Jane Smith",
    amount: 3000,
    invoiceRef: "INV-2024-003",
    date: "2024-01-20",
    type: "Individual",
  },
  {
    id: 4,
    name: "Community Alliance",
    amount: 15000,
    invoiceRef: "INV-2024-004",
    date: "2024-01-22",
    type: "Collaboration",
  },
];

const Donations = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [donationType, setDonationType] = useState("all");
  const [createCampaignModalOpen, setCreateCampaignModalOpen] = useState(false);
  const [createDonationModalOpen, setCreateDonationModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState<number | null>(null);

  const handleEdit = (id: number) => {
    setSelectedDonation(id);
    setEditModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setSelectedDonation(id);
    setDeleteModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Donations</h1>
          <p className="mt-1 text-muted-foreground">
            Manage campaigns, donors, and contributions
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setCreateCampaignModalOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Campaign
          </Button>
          <Button onClick={() => setCreateDonationModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Donation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Donations (This Year)"
          value="$145,230"
          change="+23%"
          changeType="increase"
          icon={DollarSign}
          iconColor="text-success"
        />
        <StatCard
          title="Active Donors"
          value="342"
          change="+12"
          changeType="increase"
          icon={Users}
          iconColor="text-primary"
        />
        <StatCard
          title="This Month"
          value="$18,780"
          change="+8%"
          changeType="increase"
          icon={Calendar}
          iconColor="text-accent"
        />
        <StatCard
          title="Donation Growth"
          value="+15%"
          change="vs last month"
          changeType="increase"
          icon={TrendingUp}
          iconColor="text-info"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="campaigns" className="space-y-6">
        <TabsList>
          <TabsTrigger value="campaigns">Donation Campaigns</TabsTrigger>
          <TabsTrigger value="donors">Donor List</TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="min-w-[200px] flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search campaigns..."
                      value={searchQuery}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setSearchQuery(e.target.value)
                      }
                      className="w-full rounded-md border border-input bg-background px-9 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </div>
                <select
                  value={eventFilter}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setEventFilter(e.target.value)
                  }
                  className="h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="all">All Events</option>
                  <option value="charity">Charity Gala</option>
                  <option value="fundraiser">Fundraiser 2024</option>
                  <option value="marathon">Marathon Event</option>
                </select>
                <select
                  value={donationType}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setDonationType(e.target.value)
                  }
                  className="h-10 w-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="all">All Types</option>
                  <option value="individual">Individual Donations</option>
                  <option value="corporate">Corporate Sponsorships</option>
                  <option value="collaboration">Collaboration Donations</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Donation Items Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {mockDonations.map(donation => (
              <DonationCard
                key={donation.id}
                {...donation}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </TabsContent>

        {/* Donors Tab */}
        <TabsContent value="donors">
          <DonorTable donors={mockDonors} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CreateDonationModal
        open={createCampaignModalOpen}
        onOpenChange={setCreateCampaignModalOpen}
      />
      <CreateDonationRecordModal
        open={createDonationModalOpen}
        onOpenChange={setCreateDonationModalOpen}
      />
      <EditDonationModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        donationId={selectedDonation}
      />
      <DeleteDonationModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        donationId={selectedDonation}
      />
    </div>
  );
};

export default Donations;
